/**
 * App — the auth gate. All session state lives in useAuth; the Console's tabs are
 * declared in components/ConsoleShell.jsx and fetch their own data.
 *
 * There is ONE shell. The Console has no RBAC and no view modes: it is a
 * single-audience customer surface (ADR 0004 / E06) whose only user type is the
 * paying customer, and the operator/research dual view this file used to select
 * between has been removed along with its shell, tab registry and four tabs. RBAC
 * belongs to Argus, which is a separate product with its own identity store (E07).
 */

import LoginScreen from "./components/LoginScreen";
import RegisterScreen from "./components/RegisterScreen";
import AccountClosure from "./components/AccountClosure";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PaymentStep from "./components/PaymentStep";
import ConsoleShell from "./components/ConsoleShell";
import MaintenanceNotice from "./components/MaintenanceNotice";
import useAuth from "./hooks/useAuth";

export default function App() {
  const {
    loggedIn, registrationSession, sessionKey, maintenance,
    authenticating, user, setUser, displayName, password, setPassword,
    loginLog, loginError, loginNotice, needsVerification, authView, setAuthView,
    verifyPath, verifyToken, doLogin, logout,
    handleRegistered, handleVerified, leaveVerify, resendVerification, openRegister,
    handleRegistrationComplete, handleCardOnFile,
    resendingVerification, resendCooldownRemaining,
    resetPath, resetToken, completeReset, leaveReset, requestNewResetLink,
    closurePath, closureToken, closureAction, leaveClosure,
    openForgot, closeForgot,
  } = useAuth();

  // OMEGA-1024 FR10 — maintenance, AHEAD OF EVERY OTHER GUARD. An operator has stopped
  // new workload platform-wide, so neither the authenticated shell nor the card step nor
  // any auth screen renders: the API refuses a submit and a registration, and a screen
  // that still offers them is the broken-looking product US2 exists to prevent. The two
  // emailed landings below are included deliberately, since both end in a registration
  // the gate would refuse anyway.
  //
  // Nothing is torn down to get here and nothing is restored on the way out. The session
  // and its token are untouched, so when the next poll comes back available this returns
  // to whichever view was showing, with no reload and no re-login (FR12).
  //
  // The in-flight panel rides along only for a session that can actually read jobs: a
  // registration session holds a token every customer route answers 403 to, so asking it
  // for a job list would buy a refusal rather than a panel. A logged-out visitor sees
  // the plain notice (FR21) — the notice is never a reason to put a login form on screen
  // during a stop.
  if (maintenance) {
    return (
      <MaintenanceNotice
        message={maintenance.message}
        since={maintenance.since}
        showInFlight={loggedIn && !registrationSession}
      />
    );
  }

  // L01 — the magic-link landing (routerless): /verify-email?token=<raw>. Takes
  // priority over the auth gate so the click both verifies AND signs in, and over
  // the card step below so a stored registration token cannot swallow the click.
  // handleVerified then clears the path; the token it stored is a registration
  // session, so the next render lands on the card step, not the Console.
  if (verifyPath) {
    return (
      <VerifyEmail
        token={verifyToken}
        onVerified={handleVerified}
        onBackToLogin={leaveVerify}
      />
    );
  }

  // A13 — the emailed closure link (routerless): /account-closure?token=<raw>&action=...
  // Ahead of the auth gate for the same reason the two links below are: the customer
  // following it is not signed in, and after a confirm their account cannot sign in at all.
  // The component does NOT act on arrival; `action` only preselects a button.
  if (closurePath) {
    return <AccountClosure token={closureToken} action={closureAction} onLeave={leaveClosure} />;
  }

  // F14 — the emailed reset link (routerless): /reset-password?token=<raw>. Takes
  // priority over the auth gate for the same reason the verify link does: a
  // customer who cannot sign in is exactly who follows this link, so it must not
  // sit behind a login. Unlike verify it does NOT auto-login — completeReset
  // tears the session down instead (FR10).
  if (resetPath) {
    return (
      <ResetPassword
        token={resetToken}
        onResetComplete={completeReset}
        onBackToLogin={leaveReset}
        onRequestNewLink={requestNewResetLink}
      />
    );
  }

  // E23.9 FR1 — the card step, for a session whose stored token is a registration
  // session. Ahead of the auth gate and the shell, for the reason the two landings
  // above are: a customer who cannot yet enter the Console is exactly who needs this
  // screen. Behind them, because both emailed links must keep working from here
  // (FR5). Sign-in, registration with verification off and the verify link all
  // reach it, since each stores the token this branch reads. The step leaves it
  // three ways: a completion stores a full token (the next render is the shell), a
  // card already on file signs out with a notice, and its own logout. Keyed on whose
  // session it is: another account's registration token, stored by another tab, mounts a
  // fresh step, so nothing the previous account's step still has in flight can land.
  if (loggedIn && registrationSession) {
    return (
      <PaymentStep
        key={sessionKey}
        onComplete={handleRegistrationComplete}
        onCardOnFile={handleCardOnFile}
        onLogout={logout}
      />
    );
  }

  if (!loggedIn) {
    return (
      <div>
        {authView === "register" ? (
          <RegisterScreen
            onRegistered={handleRegistered}
            onBackToLogin={() => setAuthView("login")}
          />
        ) : authView === "forgot" ? (
          <ForgotPassword onBackToLogin={closeForgot} />
        ) : (
          <LoginScreen
            user={user} setUser={setUser}
            password={password} setPassword={setPassword}
            loginLog={loginLog} onConnect={doLogin}
            onRegister={openRegister}
            error={loginError}
            authenticating={authenticating}
            needsVerification={needsVerification}
            onResendVerification={resendVerification}
            resendingVerification={resendingVerification}
            resendCooldown={resendCooldownRemaining}
            onForgotPassword={openForgot}
            notice={loginNotice}
          />
        )}
      </div>
    );
  }

  return <ConsoleShell user={user} displayName={displayName} onLogout={logout} />;
}
